// Fibernet TV API Service - Routes through backend proxy to avoid cross-origin issues
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/player`;

class ApiService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('user_info');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const token = this.getToken();

    // Use XMLHttpRequest to bypass fetch middleware that consumes response body
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const method = (options.method || 'GET').toUpperCase();
      xhr.open(method, url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      if (token) xhr.setRequestHeader('Authorization', token);
      if (options.headers) {
        Object.entries(options.headers).forEach(([k, v]) => {
          if (k.toLowerCase() !== 'content-type') xhr.setRequestHeader(k, v);
        });
      }

      xhr.onload = () => {
        const text = xhr.responseText || '';
        let data = {};
        try { data = text ? JSON.parse(text) : {}; }
        catch { data = { errors: { message: text || 'API xətası' } }; }

        if (xhr.status < 200 || xhr.status >= 300) {
          const err = new Error(data.errors?.message || data.error || `HTTP ${xhr.status}`);
          console.error('API Error:', err);
          reject(err);
          return;
        }
        resolve(data);
      };

      xhr.onerror = () => {
        const err = new Error('Network error');
        console.error('API Error:', err);
        reject(err);
      };

      xhr.ontimeout = () => reject(new Error('Request timeout'));

      xhr.send(options.body || null);
    });
  }

  // Auth
  async login(username, password) {
    const data = await this.request('/auth', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    if (data.auth_token) {
      this.setToken(data.auth_token);
      // Get user info after login
      const userInfo = await this.getUserInfo();
      localStorage.setItem('user_info', JSON.stringify(userInfo));
      return { success: true, token: data.auth_token, userInfo };
    }

    throw new Error('Giriş uğursuz oldu');
  }

  // User Info
  async getUserInfo() {
    return this.request('/user-info');
  }

  // Categories
  async getLiveCategories() {
    return this.request('/categories/live');
  }

  async getVodCategories() {
    return this.request('/categories/vod');
  }

  async getSeriesCategories() {
    return this.request('/categories/series');
  }

  // Content
  async getLiveContent(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/content/live${query ? '?' + query : ''}`);
  }

  async getVodContent(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/content/vod${query ? '?' + query : ''}`);
  }

  async getSeriesContent(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/content/series${query ? '?' + query : ''}`);
  }

  async getVodDetails(id) {
    return this.request(`/content/vod/${id}`);
  }

  async getSeriesDetails(id) {
    return this.request(`/content/series/${id}`);
  }

  // EPG
  async getXmlEpg() {
    return this.request('/xml-epg');
  }

  async getChannelEpg(streamId) {
    return this.request(`/epg/${streamId}/short`);
  }

  async getChannelFullEpg(streamId) {
    return this.request(`/epg/${streamId}`);
  }

  // Notifications
  async getNotifications(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/notifications${query ? '?' + query : ''}`);
  }

  async getNewNotifications() {
    return this.request('/notifications/get-new');
  }

  async markNotificationRead(messageId = null) {
    return this.request('/notifications/mark-read', {
      method: 'POST',
      body: messageId ? JSON.stringify({ message_id: messageId }) : '{}',
    });
  }

  // Statistics
  async getMostWatched(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/statistics/streams/most_watched${query ? '?' + query : ''}`);
  }

  // Bouquets
  async getBouquets() {
    return this.request('/bouquets');
  }

  async toggleBouquet(bouquetId, state) {
    return this.request(`/bouquet/${bouquetId}/${state}`, {
      method: 'POST',
    });
  }
}

export const api = new ApiService();
export default api;
